import ko from 'knockout';
import moment from 'moment';

import { QualityType, Competention, CostCentre } from './databaseModels';
import { resolveCdnUrl } from '../cdn-url-resolver';
import { dataUriToBlob } from '../file-helper';
import { lookupAddress } from '../postalCodeApi';
import { CustomError } from '../fetch';
import { SelectPicker } from '../custom-packages/choicesjs/selectPicker';
import { getUserDocumentOptions, UserDocumentType } from "./userDocument";
import { orderBy } from 'lodash-es';
import numeral from "numeral";
import {DocumentTypeModel} from "./documentType";

const INT32_MAX_VALUE = 2147483647;
const requestImagePlaceholder = '/images/svg/add-photo.svg';

interface RequestModelOptions {
    companyAvatarHash: string;
    costCentreIsRequired: boolean;
    hasDocumentValidityFeatureFlag: boolean;
}

interface PreviewModel {
    title: string;
    subTitle: string;
    imageSrc: string;
    dateText?: string;
    cityText: string;
}

export class RequestModel {
    id: KnockoutObservable<string>;
    options: RequestModelOptions;
    
    type: KnockoutObservable<RequestType>;

    name: KnockoutObservable<string>;
    amount: KnockoutObservable<string>;

    enableEmployeeClocks: KnockoutObservable<boolean>;
    clothingRequirements: KnockoutObservable<string>;

    travelExpenses: KnockoutObservable<boolean>;
    startDate: KnockoutObservable<moment.Moment>;
    endDate: KnockoutObservable<moment.Moment>;

    selectedQualityType: KnockoutObservable<string>;
    selectedCompetentions: KnockoutObservableArray<string>;

    qualityTypes: KnockoutObservableArray<QualityType>;
    qualityTypesExpanded: KnockoutObservable<boolean>;
    qualityTypesToShow: KnockoutComputed<QualityType[]>;
    competentions: KnockoutObservableArray<Competention>;
    hasAcceptedRegistrations: KnockoutObservable<boolean>;
    hasSignedConfirmation: KnockoutObservable<boolean>;

    imageInfoExpanded: KnockoutObservable<boolean>;
    image: KnockoutObservable<any>;
    imageHash: KnockoutObservable<string>;

    addImages: KnockoutObservable<boolean>;
    images: KnockoutObservableArray<AddOrEditRequestImageInputModel>
    existingImages: KnockoutObservableArray<AddOrEditRequestImageInputModel>

    extraInfo: KnockoutObservable<string>;

    addressInfoExpanded: KnockoutObservable<boolean>;
    address: KnockoutObservable<string>;
    houseNumber: KnockoutObservable<string>;
    postalCode: KnockoutObservable<string>;
    city: KnockoutObservable<string>;

    imageSrc: KnockoutComputed<any>;
    selectedQualityTypeName: KnockoutComputed<string>;
    filteredCompetentions: KnockoutComputed<Competention[]>;

    showTooltip: KnockoutObservable<boolean>;

    costCentres: KnockoutObservableArray<CostCentre>;
    showNewCostCentre: KnockoutObservable<boolean>;
    selectedCostCentre: KnockoutObservable<SelectPicker>;
    newCostCentre: KnockoutObservable<string>;

    addDocuments: KnockoutObservable<boolean>;
    mapDocument: KnockoutObservable<RequestFileInputModel>;
    briefingDocument: KnockoutObservable<RequestFileInputModel>;
    extraDocuments: KnockoutObservableArray<RequestFileInputModel>;
    existingDocuments: KnockoutObservableArray<RequestFileModel>;

    errors: KnockoutObservableArray<CustomError>;

    previewImageSrc: KnockoutComputed<string>;
    preview: KnockoutComputed<PreviewModel>;

    closeDate: KnockoutObservable<moment.Moment>;
    daysOfWeek: KnockoutObservableArray<DaysOfWeek>;
    hoursAvailableMin: KnockoutObservable<number>;
    hoursAvailableMax: KnockoutObservable<number>;

    maxClockHours: KnockoutObservable<string>;
    contactPerson: KnockoutObservable<string>;
    contactPersonPhone: KnockoutObservable<string>;

    requiredUserDocuments: KnockoutObservableArray<UserDocumentType>;
    userDocuments: KnockoutObservableArray<SelectPicker>;

    requiredDocumentTypes: KnockoutObservableArray<string>;
    allDocumentTypes: DocumentTypeModel[];
    documentTypes: KnockoutObservableArray<SelectPicker>;
    
    costItems: KnockoutObservableArray<RequestCostItemInputModel>;
    tempCostItems: KnockoutObservableArray<RequestCostItemInputModel>;

    constructor(model: any, options: RequestModelOptions) {
        this.options = options;
        this.errors = ko.observableArray<CustomError>();
        
        this.type = ko.observable<RequestType>(model.type ?? RequestType.Hours);
        this.type.subscribe((newValue) => {
            if(newValue === RequestType.Units) {
                this.costItems(this.tempCostItems().length > 0 ? this.tempCostItems() : [new RequestCostItemInputModel(this)]);
            } else {
                this.tempCostItems(this.costItems());
                this.costItems([]);
            }
        });
        
        this.qualityTypes = ko.observableArray<QualityType>(model.qualityTypes);
        this.qualityTypesExpanded = ko.observable<boolean>(false);
        this.qualityTypesToShow = ko.pureComputed(() => {
            if (this.qualityTypesExpanded()) {
                return this.qualityTypes();
            }

            const array = this.qualityTypes().filter((qt, index) => index < 3 || qt.id == this.selectedQualityType());

            return array;
        });

        this.costCentres = ko.observableArray<CostCentre>(model.costCentres);
        this.showNewCostCentre = ko.observable<boolean>(false);
        this.selectedCostCentre = ko.observable<SelectPicker>().extend({
            validation: {
                onlyIf: () => !this.showNewCostCentre() && this.options.costCentreIsRequired,
                validator: (val: SelectPicker) => !!val,
                message: 'Kostenplaats is verplicht'
            },
            errorHandler: { source: this.errors, id: 'costCentreId' }
        });

        this.newCostCentre = ko.observable<string>().extend({
            validation: {
                onlyIf: () => this.showNewCostCentre() && this.options.costCentreIsRequired,
                validator: (val: string) => !!val,
                message: 'Kostenplaats is verplicht'
            },
            errorHandler: { source: this.errors, id: 'newCostCentre' }
        });

        if (model.costCentreId != null || model.defaultCostCentreId != null) {
            const costCentreId = model.costCentreId ?? model.defaultCostCentreId;
            const costCentre = this.costCentres().find((c: CostCentre) => c.id === costCentreId);
            if (costCentre != null) {
                this.selectedCostCentre({
                    label: costCentre.name,
                    value: costCentre.id
                });
            }
        }

        this.mapDocument = ko.observable<RequestFileInputModel>(new RequestFileInputModel(RequestFileType.Map));
        this.briefingDocument = ko.observable<RequestFileInputModel>(new RequestFileInputModel(RequestFileType.Briefing));
        this.extraDocuments = ko.observableArray<RequestFileInputModel>([new RequestFileInputModel(RequestFileType.Other)]);
        this.existingDocuments = ko.observableArray<RequestFileModel>(model.existingDocuments ?? model.documents?.map((doc: any) => new RequestFileModel(doc)) ?? []);
        this.addDocuments = ko.observable<boolean>(this.existingDocuments().length > 0);
        this.competentions = ko.observableArray<Competention>(model.competentions);

        this.hasAcceptedRegistrations = ko.observable(model.hasAcceptedRegistrations);
        this.hasSignedConfirmation = ko.observable(model.hasSignedConfirmation);
        this.id = ko.observable<string>(model.id);

        this.amount = ko.observable<string>(model.amount)
            .extend({
                required: {
                    params: true,
                    message: 'Aantal is verplicht'
                },
                validation: {
                    validator: (val: string) => {
                        const numberValue = Number(val);

                        return numberValue > 0 && numberValue < INT32_MAX_VALUE;
                    },
                    message: 'Vul een geldig aantal in'
                },
                errorHandler: { source: this.errors, id: 'Amount' }
            });

        this.startDate = ko.observable<moment.Moment>(model.startDate || moment())
            .extend({
                validation: [
                    {
                        validator: (date: string) => !!date,
                        message: () => 'Startdatum is verplicht'
                    },
                    {
                        validator: (date: string) => !moment(date, 'DD-MM-YYYY').isBefore(moment(), 'day'),
                        message: 'Startdatum mag niet in het verleden zijn'
                    }
                ]
            });
        this.endDate = ko.observable<moment.Moment>(model.endDate)
            .extend({
                validation: [
                    {
                        validator: (endDate: string) => !!endDate,
                        message: 'Einddatum is verplicht'
                    },
                    {
                        validator: (val: string) => {
                            if (this.startDate() == null) {
                                return true;
                            }

                            const endDate = moment(val, 'DD-MM-YYYY');
                            const date = moment(this.startDate());
                            return endDate.isSameOrAfter(date, 'days');
                        },
                        message: `Einddatum mag niet vóór Startdatum zijn`
                    }
                ],
                errorHandler: { source: this.errors, id: 'EndDate' },
            });

        this.selectedQualityType = ko.observable<string>(model.selectedQualityType)
            .extend({
                required: {
                    params: true,
                    message: 'Selecteer een type ZZP-er'
                }
            });
        this.name = ko.observable<string>(model.name || '')
            .extend({
                required: {
                    params: true,
                    message: 'Projectnaam is verplicht'
                }
            });

        this.addressInfoExpanded = ko.observable<boolean>(model.addressInfoExpanded || false);
        this.address = ko.observable<string>(model.address || '')
            .extend({
                required: {
                    params: true,
                    message: 'Voer een adres voor de opdracht in'
                },
                errorHandler: { source: this.errors, id: 'Address' }
            });
        this.houseNumber = ko.observable<string>(model.houseNumber || '')
            .extend({
                required: {
                    params: true,
                    message: 'Voer een huisnummer voor de opdracht in'
                }
            });
        this.postalCode = ko.observable<string>(model.postalCode || '')
            .extend({
                required: {
                    params: true,
                    message: 'Voer een postcode voor de opdracht in'
                }
            });
        this.city = ko.observable<string>(model.city || '')
            .extend({
                required: {
                    params: true,
                    message: 'Voer een stad voor de opdracht in'
                }
            });

        this.selectedQualityType.subscribe(() => this.selectedCompetentions([]));

        this.selectedCompetentions = ko.observableArray<string>(model.selectedCompetentions ? [...model.selectedCompetentions] : []);
        this.enableEmployeeClocks = ko.observable<boolean>(model.enableEmployeeClocks || true);

        this.travelExpenses = ko.observable<boolean>(model.travelExpenses || false);

        this.imageInfoExpanded = ko.observable<boolean>(model.imageInfoExpanded || false);

        this.imageHash = ko.observable<string>(model.imageHash || '');

        this.image = ko.observable<any>({
            dataURL: ko.observable<any>(model.imageDataURL)
        });

        this.image().dataURL.subscribe(() => {
            this.imageHash('');
        });

        if (this.imageHash()) {
            this.image().file = ko.observable({ name: ' ' });
        }

        this.imageSrc = ko.pureComputed(() => {
            if (this.image().dataURL()) {
                return this.image().dataURL();
            }

            if (this.imageHash()) {
                return resolveCdnUrl(this.imageHash());
            }

            return '';
        });

        this.existingImages = ko.observableArray<AddOrEditRequestImageInputModel>(model.existingImages ?? model.images?.map((img: any) => new AddOrEditRequestImageInputModel(img)) ?? []);
        this.addImages = ko.observable<boolean>(this.existingImages().length > 0);
        this.images = ko.observableArray<AddOrEditRequestImageInputModel>(this.addImages() 
            ? orderBy(this.existingImages() ?? [], 'order', 'asc')
            : [new AddOrEditRequestImageInputModel({ order: this.existingImages().length })]);

        this.selectedQualityTypeName = ko.pureComputed(() => {
            return this.qualityTypes().find((qt: QualityType) => qt.id === this.selectedQualityType())!.name;
        });

        this.filteredCompetentions = ko.pureComputed(() => {
            const competentions = this.competentions().filter((c: Competention) => this.selectedQualityType()
                && (c.qualityTypeIds && c.qualityTypeIds.includes(this.selectedQualityType())))
                .sort((a: Competention, b: Competention) => a.examOrder - b.examOrder);
            return competentions;
        });

        this.extraInfo = ko.observable<string>(model.extraInfo || '')
            .extend({
                required: {
                    params: true,
                    message: 'Opdrachtomschrijving is verplicht'
                },
                errorHandler: { source: this.errors, id: 'ExtraInfo' }
            });
        this.clothingRequirements = ko.observable<string>(model.clothingRequirements);

        this.showTooltip = ko.observable(false);

        this.previewImageSrc = ko.pureComputed(() => {
            const imageHash = this.imageHash() != null && this.imageHash() != ''
                ? resolveCdnUrl(this.imageHash())
                : resolveCdnUrl(this.options.companyAvatarHash);
            return this.imageInfoExpanded() && this.image().dataURL() ? this.imageSrc() : imageHash;
        });

        this.preview = ko.pureComputed<PreviewModel>(() => {
            const hoursAvailableMin = this.hoursAvailableMin();
            const hoursAvailableMax = this.hoursAvailableMax();

            const hoursAvailable = (hoursAvailableMin && hoursAvailableMax)
                ? (hoursAvailableMin !== hoursAvailableMax ? `${hoursAvailableMin} - ${hoursAvailableMax}` : `${hoursAvailableMin}`)
                : (hoursAvailableMin ? `${hoursAvailableMin} - Max` : hoursAvailableMax ? `Min - ${hoursAvailableMax}` : '');

            const startDate = this.startDate();
            const endDate = this.endDate();
            const startDateText = startDate ? moment(startDate).format('D MMM') : 'Startdatum';
            const endDateText = endDate ? moment(endDate).format('D MMM') : 'Einddatum';
            const dateText = moment(startDate).isSame(endDate, 'day') ? startDateText : `${startDateText} - ${endDateText}`;

            return {
                title: `${this.selectedQualityType() ? this.selectedQualityTypeName() : 'Type ZZP-er'} ${hoursAvailable ? `(${hoursAvailable} uur)` : '(uren beschikbaar)'}`,
                subTitle: getDaysOfWeekFormatted(this.daysOfWeek()) || 'Dagen',
                imageSrc: this.previewImageSrc(),
                cityText: this.city(),
                dateText
            };
        });

        this.closeDate = ko.observable<moment.Moment>(model.closeDate)
            .extend({
                validation: [
                    {
                        validator: (val: string) => !!val,
                        message: 'Sluitdatum is verplicht'
                    },
                    {
                        validator: (val: string) => {
                            if (this.endDate() == null || val == null) {
                                return true;
                            }

                            const closeDate = moment(val, 'DD-MM-YYYY', true);
                            const endDate = moment(this.endDate(), 'DD-MM-YYYY', true);
                            return closeDate.isSameOrBefore(endDate, 'day');
                        },
                        message: `Sluitdatum mag niet na de Einddatum zijn`
                    }
                ],
                errorHandler: { source: this.errors, id: 'CloseDate' }
            });

        this.daysOfWeek = ko.observableArray<DaysOfWeek>(model.daysOfWeek)
            .extend({
                errorHandler: { source: this.errors, id: 'DaysOfWeek' }
            });

        this.hoursAvailableMax = ko.observable<number>(model.hoursAvailableMax)
            .extend({
                validation: [
                    {
                        validator: (val: string) => !!val,
                        message: 'Max is verplicht'
                    },
                    {
                        validator: (val: string) => {
                            const numberValue = Number(val);
                            return numberValue > 0 && numberValue < INT32_MAX_VALUE;
                        },
                        message: 'Vul een geldig max uren in'
                    },
                ],
                errorHandler: { source: this.errors, id: 'HoursAvailableMax' }
            });

        this.hoursAvailableMin = ko.observable<number>(model.hoursAvailableMin)
            .extend({
                validation: [
                    {
                        validator: (val: string) => !!val,
                        message: 'Min is verplicht'
                    },
                    {
                        validator: (val: string) => {
                            const numberValue = Number(val);
                            return numberValue > 0 && numberValue < INT32_MAX_VALUE;
                        },
                        message: 'Vul een geldig min uren in'
                    },
                    {
                        validator: (val: string) => {
                            if (val == null || this.hoursAvailableMax() == null) {
                                return true;
                            }
                            return Number(val) <= this.hoursAvailableMax();
                        },
                        message: 'Min moet kleiner zijn dan Max'
                    },
                ],
                errorHandler: { source: this.errors, id: 'HoursAvailableMin' }
            });

        this.maxClockHours = ko.observable<string>(model.maxClockHours ?? '800')
            .extend({
                required: {
                    params: true,
                    message: 'Maximum aantal uur is verplicht'
                },
                validation: {
                    validator: (val: string) => {
                        const numberValue = Number(val);

                        return numberValue > 0 && numberValue < INT32_MAX_VALUE;
                    },
                    message: 'Vul een geldig aantal in'
                },
                errorHandler: { source: this.errors, id: 'MaxClockHours' }
            });
        
        this.contactPerson = ko.observable<string>(model.contactPerson ?? '')
            .extend({
                required: {
                    params: true,
                    message: 'Contactpersoon is verplicht'
                },
                errorHandler: { source: this.errors, id: 'ContactPerson' }
            });
        
        this.contactPersonPhone = ko.observable<string>(model.contactPersonPhone ?? '')
            .extend({
                required: {
                    params: true,
                    message: 'Telefoonnummer contactpersoon is verplicht'
                },
                errorHandler: {source: this.errors, id: 'ContactPersonPhone'}
            });

        this.requiredUserDocuments = ko.observableArray<UserDocumentType>(model.requiredUserDocuments)
            .extend({
                errorHandler: { source: this.errors, id: 'RequiredUserDocuments' }
            });
        this.userDocuments = ko.observableArray<SelectPicker>(getUserDocumentOptions());

        this.requiredDocumentTypes = ko.observableArray<string>(model.requiredDocumentTypes);
        this.allDocumentTypes = model.documentTypes;
        this.documentTypes = ko.observableArray<SelectPicker>(model.documentTypes.map((dt: DocumentTypeModel) => ({
            label: dt.name,
            value: dt.id
        })));

        this.costItems = ko.observableArray<RequestCostItemInputModel>(model.costItems ?? [])
            .extend({
                errorHandler: { source: this.errors, id: 'CostItems' }
            });

        this.tempCostItems = ko.observableArray<RequestCostItemInputModel>();
    }

    isUserDocumentRequired(documentType: string): boolean {
        const userDocumentType = parseInt(documentType);
        return this.requiredUserDocuments().includes(userDocumentType);
    }

    isDocumentTypeRequired(documentTypeId: string): boolean {
        return this.requiredDocumentTypes().some(dt => dt === documentTypeId);
    }

    async onLeavePostalCodeOrHouseNumberField() {
        if (this.postalCode() && this.postalCode().length > 0
            && this.houseNumber() && this.houseNumber().length > 0) {
            const result = await lookupAddress(this.postalCode(), this.houseNumber());

            if (result.success) {
                this.address(result.street);
                this.city(result.town);
            }
        }
    }

    toggleTooltip() {
        this.showTooltip(!this.showTooltip());
    }

    toggleCostCentre() {
        this.showNewCostCentre(!this.showNewCostCentre());
    }

    formatDate(): string {
        return `${this.startDate().format('DD/MM/YYYY')} - ${this.endDate().format('DD/MM/YYYY')}`
    }

    addDocument() {
        const currentDocuments = this.extraDocuments();
        currentDocuments.push(new RequestFileInputModel(RequestFileType.Other));
        this.extraDocuments(currentDocuments);
    }

    removeDocument(index: number) {
        const currentDocuments = this.extraDocuments();
        currentDocuments.splice(index, 1);
        this.extraDocuments(currentDocuments);
    }

    removeExistingDocument(id: string) {
        const currentDocuments = this.existingDocuments();
        const documentToRemove = currentDocuments.find(doc => doc.id === id);
        if (documentToRemove) {
            currentDocuments.splice(currentDocuments.indexOf(documentToRemove), 1);
        }
        this.existingDocuments(currentDocuments);
    }

    addImage() {
        const currentImages = this.images();
        
        const lastImage = currentImages[currentImages.length - 1];
        if (lastImage?.imageSrc() === requestImagePlaceholder) {
            return;
        }
        
        currentImages.push(new AddOrEditRequestImageInputModel({ order: currentImages.length }));
        this.images(currentImages);
    }
    
    removeImage(index: number, id?: string) {
        const currentImages = this.images();
        currentImages.splice(index, 1);
        currentImages.forEach((img, index) => img.order = index);
        this.images(currentImages);
        
        if (id != null) {
            this.removeExistingImage(id);
        }
    }
    
    removeExistingImage(id: string) {
        const currentImages = this.existingImages();
        const imageToRemove = currentImages.find(img => img.id === id);
        if (imageToRemove) {
            currentImages.splice(currentImages.indexOf(imageToRemove), 1);
        }
        this.existingImages(currentImages);
    }

    moveImage(direction: 'left' | 'right', index: number) {
        const images = this.images();
        const targetIndex = direction === 'left' ? index - 1 : index + 1;
        
        [images[index], images[targetIndex]] = [images[targetIndex], images[index]];
        images[index].order = index;
        images[targetIndex].order = targetIndex;
        
        this.images(images);
    }
    
    addCostItem() {
        const currentCostItems = this.costItems();
        currentCostItems.push(new RequestCostItemInputModel(this));
        this.costItems(currentCostItems);
    }
    
    removeCostItem(index: number) {
        const currentCostItems = this.costItems();
        currentCostItems.splice(index, 1);
        this.costItems(currentCostItems);
    }

    copy(): RequestModel {
        return new RequestModel({
            qualityTypes: this.qualityTypes(),
            competentions: this.competentions(),
            documentTypes: this.allDocumentTypes,
            id: this.id(),
            type: this.type(),
            amount: this.amount(),
            startDate: this.startDate(),
            selectedQualityType: this.selectedQualityType(),
            selectedCompetentions: this.selectedCompetentions(),
            enableEmployeeClocks: this.enableEmployeeClocks(),
            travelExpenses: this.travelExpenses(),
            clothingRequirements: this.clothingRequirements(),
            imageInfoExpanded: this.imageInfoExpanded(),
            imageDataURL: this.image().dataURL(),
            imageHash: this.imageHash(),
            images: this.existingImages().map(img => new AddOrEditRequestImageInputModel(img)),
            name: this.name(),
            extraInfo: this.extraInfo(),
            addressInfoExpanded: this.addressInfoExpanded(),
            address: this.address(),
            houseNumber: this.houseNumber(),
            city: this.city(),
            postalCode: this.postalCode(),
            costCentres: this.costCentres(),
            costCentreId: this.selectedCostCentre()?.value,
            documents: this.existingDocuments().map(doc => new RequestFileModel(doc)),
            endDate: this.endDate(),
            closeDate: this.closeDate(),
            daysOfWeek: this.daysOfWeek(),
            hoursAvailableMin: this.hoursAvailableMin(),
            hoursAvailableMax: this.hoursAvailableMax(),
            maxClockHours: this.maxClockHours(),
            contactPerson: this.contactPerson(),
            contactPersonPhone: this.contactPersonPhone(),
            requiredUserDocuments: this.requiredUserDocuments(),
            requiredDocumentTypes: this.requiredDocumentTypes(),
            costItems: this.costItems()
        }, this.options);
    }

    toRequestData(): any {
        const startDate = this.startDate().format('YYYY-MM-DD');
        const endDate = this.endDate().format('YYYY-MM-DD');

        const data = new FormData();
        data.append('id', this.id());
        data.append('type', this.type().toString());
        data.append('name', this.name());
        data.append('amount', this.amount());
        data.append('startDate', startDate);
        data.append('endDate', endDate);
        data.append('closeDate', this.closeDate().format('YYYY-MM-DD'));
        data.append('hoursAvailableMin', this.hoursAvailableMin().toString());
        data.append('hoursAvailableMax', this.hoursAvailableMax().toString());

        data.append('qualityTypeId', this.selectedQualityType());
        data.append('enableEmployeeClocks', this.enableEmployeeClocks().toString());
        data.append('travelExpenses', this.travelExpenses().toString());
        data.append('address', this.address());
        data.append('city', this.city());
        data.append('houseNumber', this.houseNumber());
        data.append('postalCode', this.postalCode());
        data.append('maxClockHours', this.maxClockHours());

        this.daysOfWeek().forEach((value, index) => {
            data.append(`daysOfWeek[${index}]`, value.toString());
        });

        if(this.options.hasDocumentValidityFeatureFlag) {
            this.requiredDocumentTypes().forEach((value, index) => {
                data.append(`requiredDocumentTypes[${index}]`, value);
            });
        } else {
            this.requiredUserDocuments().forEach((value, index) => {
                data.append(`requiredUserDocuments[${index}]`, value.toString());
            });
        }

        if (this.imageInfoExpanded()) {
            if (this.image().file && this.image().file()) {
                data.append('image', this.image().file());
            } else if (this.image().dataURL()) {
                const blob = dataUriToBlob(this.image().dataURL());
                data.append('image', blob);
            } else if (this.imageHash() != null && this.imageHash() != '') {
                fetch(this.imageSrc())
                    .then(res => res.blob())
                    .then(blob => {
                        data.append('image', blob);
                    });
            }
            data.append('imageHash', this.imageHash());
        }

        this.contactPerson() != null && data.append('contactPerson', this.contactPerson());
        this.contactPersonPhone() != null && data.append('contactPersonPhone', this.contactPersonPhone());
        
        this.extraInfo() != null && data.append('extraInfo', this.extraInfo());
        this.clothingRequirements() != null && data.append('clothingRequirements', this.clothingRequirements());
        if (this.selectedCompetentions().length > 0) {
            this.selectedCompetentions().forEach((c: string) => data.append('competentions[]', c));
        }

        if (this.showNewCostCentre() && this.newCostCentre() != null) {
            data.append('newCostCentre', this.newCostCentre());
        } else if (!this.showNewCostCentre() && this.selectedCostCentre() != null) {
            data.append('costCentreId', this.selectedCostCentre().value);
        }

        if (this.addDocuments()) {
            let documents = this.extraDocuments()
                .filter((doc) => doc.file().file() != null);

            if (this.mapDocument().file()?.file() != null) {
                documents.push(this.mapDocument())
            }

            if (this.briefingDocument().file()?.file() != null) {
                documents.push(this.briefingDocument());
            }

            documents.forEach((doc, index) => {
                data.append(`documents[${index}].file`, doc.file().file());
                data.append(`documents[${index}].type`, doc.type.toString());
            });

            this.existingDocuments().forEach(doc => {
                data.append('existingDocumentIds', doc.id);
            });
        }

        if (this.addImages()) {
            const newImages = this.images().filter(img => img.id == null && img.file() != null);
            const existingImages = this.existingImages().filter(img => img.id != null);

            newImages.forEach(((img, index) => {
                data.append(`newImages[${index}].file`, img.file());
                data.append(`newImages[${index}].order`, img.order.toString());
            }));

            existingImages.forEach((img, index) => {
                data.append(`existingImages[${index}].fileId`, img.id);
                data.append(`existingImages[${index}].order`, img.order.toString());
            });
        }
        
        if(this.type() === RequestType.Units) {
            this.costItems().forEach((costItem, index) => {
                if(costItem.id() != null) {
                    data.append(`costItems[${index}].id`, costItem.id());
                }
                
                data.append(`costItems[${index}].name`, costItem.name());
                data.append(`costItems[${index}].unitName`, costItem.unitName());
                data.append(`costItems[${index}].unitPrice`, numeral(costItem.unitPrice()).format('0.00'));
            });
        }

        return data;
    }
}

export class AddOrEditRequestImageInputModel {
    id: string;
    name: string;
    cdnHash: string;

    order: number
    file: KnockoutObservable<File>;
    dataURL: KnockoutObservable<string>;

    imageAlt: KnockoutComputed<string>;
    imageSrc: KnockoutComputed<string>;

    buttonClass: KnockoutComputed<string | null>;
    customFileInputBinding: KnockoutObservable<any>;

    constructor(model: any) {
        this.id = model.id;
        this.name = model.name;
        this.cdnHash = model.cdnHash;

        this.order = model.order;
        this.file = ko.observable<File>(null);
        this.dataURL = ko.observable<string>();

        this.imageAlt = ko.pureComputed(() => this.file()?.name ?? this.name ?? 'Add photo');
        this.imageSrc = ko.pureComputed(() => this.cdnHash ? resolveCdnUrl(this.cdnHash) : this.dataURL() || requestImagePlaceholder);

        this.buttonClass = ko.pureComputed(() => this.cdnHash ? null : 'move-button')
        this.customFileInputBinding = ko.observable({
            wrapperClass: 'form-file',
            buttonClass: this.buttonClass(),
            buttonText: '<i class="fa fa-plus"></i>',
            changeButtonText: '<i class="fa fa-pencil"></i>',
            clearButton: false,
        });
    }
}

export enum RequestFileType {
    Map = 1,
    Briefing = 2,
    Other = 3
}

export class RequestFileInputModel {
    file: KnockoutObservable<any>;
    type: RequestFileType

    constructor(type: RequestFileType) {
        this.file = ko.observable<any>();
        this.type = type;
    }
}

export class RequestFileModel {
    id: string;
    name: string;
    type: RequestFileType;

    constructor(model: any) {
        this.id = model.id;
        this.name = model.name;
        this.type = model.type;
    }
}

export class RequestCostItemInputModel {
    parent: RequestModel;

    id: KnockoutObservable<string>;
    name: KnockoutObservable<string>;
    unitName: KnockoutObservable<string>;
    unitPrice: KnockoutObservable<string>;
    unitPriceFormatted: KnockoutComputed<string>;
    
    constructor(parent: RequestModel, model?: any) {
        this.parent = parent;
        
        this.id = ko.observable<string>(model?.id ?? '');
        this.name = ko.observable<string>(model?.name ?? '')
            .extend({
                validation: {
                    onlyIf: () => this.parent.type() === RequestType.Units,
                    validator: (val: string) => !!val,
                    message: 'Naam is verplicht'
                },
            });
        this.unitName = ko.observable<string>(model?.unitName ?? '')
            .extend({
                validation: {
                    onlyIf: () => this.parent.type() === RequestType.Units,
                    validator: (val: string) => !!val,
                    message: 'Stuk naam is verplicht'
                },
            });
        this.unitPrice = ko.observable<string>(model?.unitPrice ?? '')
            .extend({
                validation: [
                    {
                        onlyIf: () => this.parent.type() === RequestType.Units,
                        validator: (val: string) => !!val,
                        message: 'Stukprijs is verplicht'
                    },
                    {
                        onlyIf: () => this.parent.type() === RequestType.Units,
                        validator: (val: string) => {
                            return numeral.validate(val, 'nl-nl');
                        },
                        message: 'Vul een geldig stukprijs in'
                    },
                    {
                        onlyIf: () => this.parent.type() === RequestType.Units,
                        validator: (val: string) => {
                            const numberValue = numeral(val);

                            return numberValue.value() >= 0.01;
                        },
                        message: () => `Stukprijs moet minstens ${numeral(0.01).format('$0,0.00')} per uur zijn`
                    },
                    {
                        // The price is what the company pays and is invoiced at two decimals.
                        // It was previously truncated silently on submit; say so instead.
                        onlyIf: () => this.parent.type() === RequestType.Units,
                        validator: (val: string) => {
                            const numberValue = numeral(val).value();

                            return numberValue === null
                                || Math.round(numberValue * 100) / 100 === numberValue;
                        },
                        message: 'Stukprijs mag maximaal 2 decimalen hebben'
                    }
                ]
            });
        
        this.unitPriceFormatted = ko.pureComputed(() => {
            return numeral(this.unitPrice()).format('$0,0.00');
        });
    }
}

export class RequestCostItemModel {
    id: string;
    name: string;
    unitName: string;
    unitPrice: number;

    constructor(model: any) {
        this.id = model.id;
        this.name = model.name;
        this.unitName = model.unitName;
        this.unitPrice = model.unitPrice;
    }
    
    formattedString(): string {
        return `${this.name}  - € ${numeral(this.unitPrice).format('0,0.00')} per ${this.unitName}`;
    }
}

export enum RequestType {
    Hours = 0,
    Units = 1
}

export enum DaysOfWeek {
    Monday = 1,
    Tuesday = 2,
    Wednesday = 4,
    Thursday = 8,
    Friday = 16,
    Saturday = 32,
    Sunday = 64
}

export const getDaysOfWeekFormatted = (daysOfWeek: DaysOfWeek[]) => {
    return daysOfWeek
        .sort((a: DaysOfWeek, b: DaysOfWeek) => (a - b))
        .map((day: DaysOfWeek, index: number) => {
            let dayLabel = '';

            switch (parseInt(day.toString())) {
                case DaysOfWeek.Monday:
                    dayLabel = 'Ma';
                    break;
                case DaysOfWeek.Tuesday:
                    dayLabel = 'Di';
                    break;
                case DaysOfWeek.Wednesday:
                    dayLabel = 'Wo';
                    break;
                case DaysOfWeek.Thursday:
                    dayLabel = 'Do';
                    break;
                case DaysOfWeek.Friday:
                    dayLabel = 'Vr';
                    break;
                case DaysOfWeek.Saturday:
                    dayLabel = 'Za';
                    break;
                case DaysOfWeek.Sunday:
                    dayLabel = 'Zo';
                    break;
                default:
                    break;
            }

            return index === 0
                ? dayLabel.charAt(0).toUpperCase() + dayLabel.slice(1)
                : dayLabel.toLowerCase();
        }).join(', ')
}
